/**
 * What a KPI dashboard has already taught this browser (DOP-609).
 *
 * Three facts, each remembered so the dashboard stops explaining itself once
 * it has been understood:
 *
 * - `guideVisible` — the "How to read a cell" key above the grid. ON until the
 *   user closes it. The Summit call (9/23) found the old on-demand popover was
 *   never discovered, so the key now starts on screen and leaving it is the
 *   user's choice, not the default.
 * - `tourSeen` — the three-step tour runs once, on the first visit with data.
 *   "Take the tour" replays it, and replaying also brings the key back: the
 *   tour's first step points at it.
 * - `chartsSeen` — the Charts tab carries a "New" badge until it is opened.
 *
 * `localStorage` for the same reasons `useHideEmptyRows` gives: a per-person
 * view preference, and every access guarded so blocked storage degrades to
 * "show everything again" rather than a crash. Per PERSON, so the keys carry
 * the signed-in user's id: a second account on the same browser has not seen
 * the tour just because a colleague has. Deliberately NOT per workflow — the
 * tour teaches the dashboard, not one workflow's numbers, and replaying it on
 * every new workflow would be the nag it exists not to be.
 */

import { useCallback, useEffect, useState } from "react";

import { useOptionalAuth } from "../context/AuthContext";

export const KPI_GUIDE_HIDDEN_KEY = "doppler.kpi.cellGuideHidden";
export const KPI_TOUR_SEEN_KEY = "doppler.kpi.tourSeen";
export const KPI_CHARTS_SEEN_KEY = "doppler.kpi.chartsSeen";

/** The stored key for one flag, scoped to the signed-in user. */
export function kpiOnboardingKey(base: string, userId: string | null | undefined): string {
	return `${base}:${userId ?? "anonymous"}`;
}

function readFlag(key: string): boolean {
	if (typeof window === "undefined") return false;
	try {
		return window.localStorage.getItem(key) === "true";
	} catch {
		return false;
	}
}

function writeFlag(key: string, value: boolean): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(key, String(value));
	} catch {
		// Unusable storage: the dashboard just explains itself again next time.
	}
}

export interface KpiOnboardingState {
	/**
	 * False while sign-in is still resolving: the flags read so far belong to
	 * nobody yet, so nothing may START on them (the first-visit tour).
	 */
	ready: boolean;
	guideVisible: boolean;
	hideGuide: () => void;
	tourSeen: boolean;
	/** Opens the tour; also brings the cell key back, which step 1 points at. */
	tourOpen: boolean;
	startTour: () => void;
	endTour: () => void;
	chartsSeen: boolean;
	markChartsSeen: () => void;
}

export function useKpiOnboarding(): KpiOnboardingState {
	const auth = useOptionalAuth();
	const userId = auth?.user?.id;
	// No provider (an isolated render) is as resolved as it will ever get;
	// under one, wait until the user is known.
	const ready = auth === undefined || (!auth.loading && Boolean(auth.user));
	const guideKey = kpiOnboardingKey(KPI_GUIDE_HIDDEN_KEY, userId);
	const tourKey = kpiOnboardingKey(KPI_TOUR_SEEN_KEY, userId);
	const chartsKey = kpiOnboardingKey(KPI_CHARTS_SEEN_KEY, userId);

	const [guideVisible, setGuideVisible] = useState(() => !readFlag(guideKey));
	const [tourSeen, setTourSeen] = useState(() => readFlag(tourKey));
	const [tourOpen, setTourOpen] = useState(false);
	const [chartsSeen, setChartsSeen] = useState(() => readFlag(chartsKey));

	// The identity can arrive after mount (sign-in resolving) or change under
	// a mounted page (log out, log in as someone else). Either way the flags
	// on screen must be the NEW person's, and a tour opened for the old one
	// closes rather than carrying over.
	const [loadedFor, setLoadedFor] = useState(userId);
	useEffect(() => {
		if (loadedFor === userId) return;
		setLoadedFor(userId);
		setGuideVisible(!readFlag(guideKey));
		setTourSeen(readFlag(tourKey));
		setChartsSeen(readFlag(chartsKey));
		setTourOpen(false);
	}, [userId, loadedFor, guideKey, tourKey, chartsKey]);

	const hideGuide = useCallback(() => {
		setGuideVisible(false);
		writeFlag(guideKey, true);
	}, [guideKey]);

	const startTour = useCallback(() => {
		setGuideVisible(true);
		writeFlag(guideKey, false);
		setTourOpen(true);
	}, [guideKey]);

	// Seen as soon as it ends, however it ends: skipping is an answer too, and
	// a tour that comes back after "Skip" is the nag it was meant not to be.
	const endTour = useCallback(() => {
		setTourOpen(false);
		setTourSeen(true);
		writeFlag(tourKey, true);
	}, [tourKey]);

	const markChartsSeen = useCallback(() => {
		setChartsSeen(true);
		writeFlag(chartsKey, true);
	}, [chartsKey]);

	return {
		ready: ready && loadedFor === userId,
		guideVisible,
		hideGuide,
		tourSeen,
		tourOpen,
		startTour,
		endTour,
		chartsSeen,
		markChartsSeen,
	};
}
